import { AxiosResponse } from "axios";
import HttpService from "src/services/httpService";
import RouteService from "src/services/routeService";
import { ApiResponse, BasicApiResponse, ListResponse, PaginatedResponse } from "src/types";

import { MdmCommand } from "@dashboard/devices/types/commands";

import { unpackError } from "./helpers";

export type UpdateDevicePayload = {
    id: number;
    name: string | null;
    description: string | null;
    comment: string | null;
};
export const updateDevice = async ({ id, ...payload }: UpdateDevicePayload) => {
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
        if (value !== null) {
            formData.append(key, value);
        }
    });

    try {
        const endpoint = await RouteService.getDevicesRoute();
        await HttpService.patch<FormData, ApiResponse<API.License>>(endpoint + String(id), formData);
    } catch (error) {
        throw unpackError(error);
    }
};

type CreateDevicePayload = {
    androidSerial: string;
    productName: string;
    groupId: number;
    mac?: string;
};
export type CreateDeviceResponse = ApiResponse<{
    identityDataHex: string | null;
    identityNonceHex: string | null;
    license: API.License;
}>;
export const createDevice = async (payload: CreateDevicePayload): Promise<CreateDeviceResponse> => {
    try {
        const endpoint = await RouteService.getDevicesRoute();
        const { data } = await HttpService.put<CreateDevicePayload, AxiosResponse<CreateDeviceResponse>>(endpoint, {
            identityJson: { productName: payload.productName, androidSerial: payload.androidSerial, mac: payload.mac },
            groupId: payload.groupId,
        });
        return data;
    } catch (error) {
        throw unpackError(error);
    }
};
interface FetchDeviceMdmCommandsPayload {
    deviceId: number;
    pageIndex?: number;
    pageSize?: number;
    sortCriteria?: string;
    sortDirection?: string;
}
export const fetchDeviceMdmCommands = async (payload: FetchDeviceMdmCommandsPayload) => {
    try {
        const endpoint = await RouteService.getDevicesRoute();
        const { data } = await HttpService.get<string, AxiosResponse<PaginatedResponse<MdmCommand>>>(
            endpoint + `${payload.deviceId}/commands`,
            {
                params: {
                    pageIndex: payload.pageIndex || 1,
                    pageSize: payload.pageSize || 24,
                    ...(payload.sortCriteria && { sortCriteria: payload.sortCriteria }),
                    ...(payload.sortDirection && { sortDirection: payload.sortDirection }),
                },
            },
        );
        return data;
    } catch (error) {
        throw unpackError(error);
    }
};
interface CreateDeviceMdmCommandPayload {
    deviceId: number;
    json: Record<string, unknown>;
    commandType?: string;
    commandParameter?: string;
}
export const createDeviceMdmCommand = async (payload: CreateDeviceMdmCommandPayload) => {
    try {
        const endpoint = await RouteService.getDevicesRoute();
        const { data } = await HttpService.post<string, AxiosResponse<ListResponse<MdmCommand>>>(
            endpoint + `${payload.deviceId}/commands`,
            { payload: payload.json },
            {
                headers: { "content-type": "application/json" },
            },
        );
        return data.list;
    } catch (error) {
        throw unpackError(error);
    }
};

interface DeleteDeviceMdmCommandPayload {
    deviceId: number;
    commandId: number;
}
export const deleteDeviceMdmCommand = async (payload: DeleteDeviceMdmCommandPayload) => {
    try {
        const endpoint = await RouteService.getDevicesRoute();
        const response = await HttpService.delete<string, AxiosResponse<BasicApiResponse>>(
            endpoint + `${payload.deviceId}/commands/${payload.commandId}`,
        );
        return response;
    } catch (error) {
        throw unpackError(error);
    }
};

interface FetchDevicePayload {
    deviceId: number;
}
export const fetchDevice = async (payload: FetchDevicePayload) => {
    try {
        const endpoint = await RouteService.getDevicesRoute();
        const { data } = await HttpService.get<string, AxiosResponse<ApiResponse<API.Device>>>(endpoint + `${payload.deviceId}`);
        return data.model;
    } catch (error) {
        throw unpackError(error);
    }
};
